import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { MDTypography, FaIcon, MDBox } from '@components';
import {
  Grid,
  Switch,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { useMaterialUIController } from '@context/MDContext';
import SelectThemeMenu from '../../SelectThemeMenu';
import { VisualSettings } from '@DStypes/chart';

import { RootState } from '@store/store';
import { sendAmplitudeData } from '@utils/amplitude';
import { SET_3D, SET_GRADIENT, SET_MULTI_COLOUR } from '@constants/eventNames';

interface Props {
  selectedTheme: string[] | null;
  setSelectedTheme: (colors: string[] | null, disableTheme?: boolean) => void;
  setFilters: React.Dispatch<React.SetStateAction<VisualSettings | null>>;
  filters: VisualSettings | null;
  chartType: string;
  hasSegments: boolean;
}

const ThemeMenu: React.FC<Props> = ({
  setSelectedTheme,
  setFilters,
  filters,
  chartType,
  hasSegments,
  selectedTheme
}) => {
  const [controller] = useMaterialUIController();
  const { preventClosing } = controller;

  const [chart3dToggle, set3dChartToggle] = useState(false);
  const [gradientChartToggle, setGradientChartToggle] = useState(false);
  const [multiColorChartToggle, setMultiColorChartToggle] = useState(false);

  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [themeTooltip, setThemeTooltip] = useState('Click to add a Theme');

  const projectData = useSelector(
    (state: RootState) => state?.projectBoard.currentProjectData
  );
  const isEmptyColorSet =
    projectData?.colorset === null && selectedTheme === null;

  const [selectThemeToggle, setSelectThemeToggle] = useState(
    !!filters?.colorThemeToggle && !isEmptyColorSet
  );

  const disableMultiColor =
    hasSegments ||
    chartType === 'PIE' ||
    chartType === 'Line' ||
    chartType === 'KPI';

  const setChartVisualSettings = async (
    key: string,
    value: boolean,
    isConnected?: boolean
  ) => {
    setFilters((prev) => ({
      ...prev,
      ['3d_charts']: chart3dToggle,
      gradient_charts: gradientChartToggle,
      multicolor_charts: multiColorChartToggle,
      colorThemeToggle: selectThemeToggle,
      theme_connection_status: {
        ...(prev?.theme_connection_status || {
          ['3d_charts']: true,
          gradient_charts: true,
          multicolor_charts: true,
          colorThemeToggle: true
        }),
        [key]: isConnected
      },
      [key]: value
    }));
  };

  // hook to initiate visual settings toggles
  useEffect(() => {
    set3dChartToggle(
      filters?.theme_connection_status?.['3d_charts']
        ? !!projectData?.visual_settings?.['3d_charts']
        : !!filters?.['3d_charts']
    );
    setGradientChartToggle(
      filters?.theme_connection_status?.gradient_charts
        ? !!projectData?.visual_settings?.gradient_charts
        : !!filters?.gradient_charts
    );
    setMultiColorChartToggle(
      filters?.theme_connection_status?.multicolor_charts
        ? !!projectData?.visual_settings?.multicolor_charts
        : !!filters?.multicolor_charts
    );
    setSelectThemeToggle(
      filters?.theme_connection_status?.colorThemeToggle
        ? !!projectData?.colorset
        : !!filters?.colorThemeToggle
    );
  }, [filters]);

  return (
    <MDBox display='flex' flexDirection='column' width='340px'>
      <Grid container alignItems={'center'} width='95%'>
        <Grid container alignItems={'center'} width='100%'>
          <Grid container alignItems='center' height='36px' marginBottom={1}>
            <Grid container direction='column'>
              <Grid item height='45px'>
                <MDBox sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip
                    title={
                      chartType === 'Line'
                        ? 'Change chart type from Line to enable 3D view for chart'
                        : chartType === 'KPI'
                        ? 'Sorry, this option is unavailable for KPI.'
                        : ''
                    }
                    placement='top'
                    sx={{ pointerEvents: 'auto' }}
                  >
                    <div>
                      <Switch
                        checked={!(chartType === 'Line') && chart3dToggle}
                        disabled={chartType === 'Line' || chartType === 'KPI'}
                        onChange={(e, value) => {
                          setChartVisualSettings(
                            '3d_charts',
                            value,
                            projectData?.visual_settings?.['3d_charts'] ===
                              value
                          );
                          if (value) sendAmplitudeData(SET_3D);
                        }}
                      />
                    </div>
                  </Tooltip>
                  <MDTypography
                    variant='sm'
                    fontWeight='regular'
                    mt={0.2}
                    color='info'
                    ml={1}
                    opacity={
                      chartType === 'Line' || chartType === 'KPI' ? 0.5 : 1
                    }
                  >
                    {`Show ${chartType === 'KPI' ? 'KPI' : 'Chart'} as 3D`}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item height='45px'>
                <MDBox sx={{ display: 'block', alignItems: 'center' }}>
                  <Switch
                    checked={gradientChartToggle}
                    onChange={(e, value) => {
                      setChartVisualSettings(
                        'gradient_charts',
                        value,
                        projectData?.visual_settings?.gradient_charts === value
                      );
                      if (value) sendAmplitudeData(SET_GRADIENT);
                    }}
                    disabled={chartType === 'KPI' && !filters?.kpiTarget}
                  />
                  <MDTypography
                    variant='sm'
                    fontWeight='regular'
                    mt={0.2}
                    color='info'
                    ml={1}
                    opacity={
                      chartType === 'KPI' && !filters?.kpiTarget ? 0.5 : 1
                    }
                  >
                    {`Show ${
                      chartType === 'KPI' ? 'KPI' : 'Chart'
                    } as Gradient`}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item height='45px'>
                <MDBox sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip
                    title={
                      chartType === 'KPI'
                        ? 'Sorry, this option is unavailable for KPI.'
                        : ''
                    }
                    placement='top'
                  >
                    <div>
                      <Switch
                        checked={
                          multiColorChartToggle &&
                          !hasSegments &&
                          chartType !== 'PIE' &&
                          chartType !== 'Line'
                        }
                        onChange={(e, value) => {
                          setChartVisualSettings(
                            'multicolor_charts',
                            value,
                            projectData?.visual_settings?.multicolor_charts ===
                              value
                          );
                          if (value) sendAmplitudeData(SET_MULTI_COLOUR);
                        }}
                        disabled={disableMultiColor}
                      />
                    </div>
                  </Tooltip>

                  <MDTypography
                    variant='sm'
                    fontWeight='regular'
                    mt={0.2}
                    color='info'
                    ml={1}
                    opacity={disableMultiColor ? 0.5 : 1}
                  >
                    {`Show  Multi-colour ${
                      chartType === 'KPI' ? 'KPI' : 'Chart'
                    }`}
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>
            <Grid item xs={11} alignItems='center' height='45px'>
              <Switch
                checked={selectThemeToggle}
                onChange={(e, value) => {
                  setThemeTooltip(
                    value === true
                      ? 'Click to remove theme'
                      : 'Click to add a theme'
                  );
                  setShowThemeMenu(value);
                  setChartVisualSettings(
                    'colorThemeToggle',
                    value,
                    value ? false : undefined
                  );
                  if (!value) setSelectedTheme(null, true);
                }}
              />
              <Tooltip title={themeTooltip} placement='top'>
                <MDTypography
                  variant='sm'
                  fontWeight='regular'
                  mt={0.2}
                  color='info'
                  ml={1}
                  mr={0.5}
                >
                  Select a Color Palette
                </MDTypography>
              </Tooltip>
            </Grid>
            {selectThemeToggle && (
              <Grid item xs={1}>
                {preventClosing ? (
                  <CircularProgress color='secondary' size='20px' />
                ) : (
                  <Tooltip
                    title={
                      showThemeMenu === true ? 'Click to close Theme menu' : ''
                    }
                    placement='top'
                  >
                    <IconButton
                      onClick={() => setShowThemeMenu((prev) => !prev)}
                    >
                      <FaIcon
                        icon={'chevron-down'}
                        typographyProps={{ color: 'info', variant: 'xl' }}
                        style={{
                          transition: 'all .3s ease-in-out',
                          transform: `rotate(${showThemeMenu ? '180deg' : 0})`
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
            )}
            <>
              {showThemeMenu && selectThemeToggle && (
                <SelectThemeMenu setSelectedTheme={setSelectedTheme} />
              )}
            </>
          </Grid>
        </Grid>
      </Grid>
    </MDBox>
  );
};

export default ThemeMenu;
